import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useNavigate, useParams, Link} from 'react-router-dom';
import {licenseSetter} from '@ey/canvascoreservices/AGGridEnterprise';
import {LicenseManager} from 'ag-grid-enterprise';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {EYLogo} from '@ey/canvascoreservices';
import {MotifIcon, navigationIcMenu24px} from '@common/MotifWrapperComponents/motifCompat';
import {isEmpty} from 'lodash';
import {labels, setLanguage} from '@util/utils';
import {modalSource, routesResources} from '@util/uiconstants';
import {ProjectSelect} from '../ProjectSelect/ProjectSelect';
import NavUser from './NavUser';
import {selectMetadata, selectAdminUser} from '@features/admin/admin-selector';
import {toggleUnsavedModal} from '../UnsavedChangesModal/UnsavedChangesModal';
import {getFeatureConfigurations} from './NavActions';

import {adobeAnalytics} from '@ey/voltron-analytics';
import {clickActionArgs} from '@common/AdobeAnalytics/actionHelper';
import {eventTypes, controlTypes, locationInPages, clickActionNames} from '@common/AdobeAnalytics/constants';

const Nav = () => {
	const {cardId, partitionKey} = useParams();

	const user = useSelector((store) => store.user);
	const featureConfigurations = useSelector((store) => store.configuration);
	const admin = useSelector(selectAdminUser);

	const metadata = useSelector(selectMetadata(cardId));

	const [isModalVisible, setModalVisibility] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const trackEvent = adobeAnalytics.useAdobeAnalyticsTrack();

	function initializeLicensing(licenseKey) {
		if (!licenseKey) {
			console.warn('AG Grid Enterprise license key is missing; skipping license initialization.');
			return;
		}

		LicenseManager.setLicenseKey(licenseKey);

		licenseSetter(licenseKey);
	}

	useEffect(() => {
		if (featureConfigurations?.agGridLicenseKey) {
			initializeLicensing(featureConfigurations.agGridLicenseKey);
		}
	}, [featureConfigurations]);

	useEffect(() => {
		if (user) {
			setLanguage(user.languageIsoCode);
		}

		if (admin) {
			setLanguage(admin.languageIsoCode);
		}
	}, [user, admin]);

	useEffect(() => {
		getConfigFeatures();
	}, []);

	const getConfigFeatures = () => {
		dispatch(getFeatureConfigurations());
	};

	const validEconomicsUser = useMemo(() => {
		return !!(user && 'id' in user && !isEmpty(user.id));
	}, [user]);

	const validAdminUser = useMemo(() => {
		return !!(admin && 'id' in admin && !isEmpty(admin.id));
	}, [admin]);

	const {validUser, pathName} = useMemo(() => {
		return {
			validUser: validEconomicsUser ? validEconomicsUser : validAdminUser,
			pathName: validEconomicsUser ? '/' : `/${routesResources.admin}`
		};
	}, [validEconomicsUser, validAdminUser]);

	const onClick = useCallback(
		(event) => {
			event.preventDefault();

			const to = {
				pathname: `${pathName}`,
				state: {
					doNotReload: true,
					getAllProjects: true
				}
			};

			if (metadata?.hasChanges) {
				toggleUnsavedModal(modalSource.logo, {partitionKey});
			} else {
				navigate(to.pathname, {state: to.state});
			}

			// Adobe Analytics Click Tracking
			trackEvent(eventTypes.clickAction, clickActionArgs(clickActionNames.canvasEconomics, controlTypes.icon, locationInPages.topNav));
		},
		[metadata, partitionKey, pathName, modalSource]
	);

	return (
		<StyledNavbar className="styled-nav-bar">
			<section className="mainResponsiveWrapper">
				<section className="nav-bar__menu-icon" aria-hidden="true">
					<MotifIcon src={navigationIcMenu24px} />
				</section>
				<section className="nav-bar__ey-logo">
					{validUser ? (
						<Link onClick={onClick}>
							<span className="nav-bar__ey-logo__container">
								<EYLogo logoType="" />
							</span>
							<h1 className="nav-bar__ey-logo__text">{labels.canvasEconomics}</h1>
						</Link>
					) : (
						<span>
							<span className="nav-bar__ey-logo__container">
								<EYLogo logoType="" />
							</span>
							<h1 className="nav-bar__ey-logo__text">{labels.canvasEconomics}</h1>
						</span>
					)}
				</section>
				<ProjectSelect />
				<NavUser
					validEconomicsUser={validEconomicsUser}
					validAdminUser={validAdminUser}
					user={user}
					admin={admin}
					setIsOpen={setIsOpen}
					isOpen={isOpen}
					isModalVisible={isModalVisible}
					setModalVisibility={setModalVisibility}
					hasChanges={metadata?.hasChanges}
					partitionKey={partitionKey}
				/>
			</section>
		</StyledNavbar>
	);
};

export default Nav;

const StyledNavbar = styled.section`
	height: var(--px-56);
	width: 100%;
	border: none;
	background-color: var(--neutrals-bg-gradient1);
	display: flex;
	position: relative;
	border-bottom: var(--px-1) solid var(--neutrals-100);
	z-index: 100;

	.mainResponsiveWrapper {
		display: flex;
		padding: 0 var(--px-8);
		width: 100%;
		align-items: center;
	}

	.nav-bar__menu-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--px-8);
		margin-right: var(--px-4);
		color: var(--neutrals-900);
		cursor: pointer;
		border-radius: var(--px-44);
		.motif-icon svg {
			width: var(--px-20);
			height: var(--px-20);
		}
	}

	.delegate-btn {
		display: inherit;
		.user-initials {
			border-radius: 50%;
			width: var(--px-24);
			height: var(--px-24);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			line-height: var(--px-12);
			background-color: var(--blue-500);
			color: var(--neutrals-00white);
			font-size: var(--px-10);
			font-weight: 700;
		}
	}

	.nav-bar__ey-logo {
		vertical-align: top;
		margin: 0;
		display: inline-flex;
		user-select: none;
		align-items: center;
		a,
		> span {
			display: inline-flex;
			align-items: center;
			text-decoration: none;
		}
		.nav-bar__ey-logo__container {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: var(--px-40);
			height: var(--px-40);
			background-color: var(--neutrals-900);
			border-radius: var(--px-4);
			flex-shrink: 0;
			figure {
				height: var(--px-24);
				width: var(--px-24);
				vertical-align: unset;
				display: flex;
				align-items: center;
				justify-content: center;
				.EYLogoSVG {
					.path2,
					.path3 {
						fill: var(--neutrals-00white);
					}
				}
			}
		}
		.nav-bar__ey-logo__text {
			font-size: var(--px-16);
			display: inline-block;
			font-weight: 400;
			line-height: var(--px-24);
			margin: 0 var(--px-12);
			color: var(--neutrals-900);
			white-space: nowrap;
		}
	}

	.nav-bar_right-wrapper {
		margin-left: auto;
		padding: var(--px-8) var(--px-10) var(--px-8) 0;
		height: 100%;
		.nav-bar__user-profile {
			position: relative;
			display: inline-flex;
			max-width: var(--px-300);
			width: auto;
			font-size: var(--px-14);
			height: 100%;
			align-items: center;
		}

		.nav-bar__user-profile__menu {
			background-color: var(--neutrals-200);
			border: var(--px-1) solid var(--neutrals-300);
			width: var(--px-210);
			position: absolute;
			right: var(--px-40);
			top: var(--px-50);
			overflow: hidden;
			transition: all 0.3s;
			z-index: 9;
			font-size: var(--px-14);

			.nav-bar__user-profile__menu__links {
				display: block;
				padding: var(--px-10) var(--px-15);
				color: var(--neutrals-900);
				border-top: var(--px-1) solid var(--neutrals-200);
				cursor: pointer;
				text-decoration: none;
				:hover {
					text-decoration: underline;
				}
				&.userProfilePhoto {
					background: var(--neutrals-00white);
					svg {
						width: 60%;
						height: 60%;
						max-width: 100%;
						max-height: 100%;
						margin: 0 auto;
						display: block;
					}
				}
			}
		}
	}
`;
